import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Google as GoogleIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import { useApi } from '../hooks/useApi';
import GoogleIdPList from '../components/settings/GoogleIdPList';
import MicrosoftIdPList from '../components/settings/MicrosoftIdPList';
import CustomIdPList from '../components/settings/CustomIdPList';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../animations/variants';
import { isCustomerSubdomain, getCustomerIdFromSubdomain } from '../utils/constants';

// Theme color for Identity Providers page - using Google blue as primary
const accentColor = '#4285F4';
const accentColorLight = '#E8F0FE';

const IdentityProviders = () => {
  const [tabValue, setTabValue] = useState(0);
  const [isCustomerContext, setIsCustomerContext] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUpdatingLoginSettings, setIsUpdatingLoginSettings] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const {
    // Google IdP
    googleIdPs,
    googleIdPsLimit,
    createGoogleIdP,
    listGoogleIdPs,
    updateGoogleIdP,
    deleteGoogleIdP,
    validateGoogleIdPConfig,
    // Microsoft IdP
    microsoftIdPs,
    microsoftIdPsLimit,
    createMicrosoftIdP,
    listMicrosoftIdPs,
    updateMicrosoftIdP,
    deleteMicrosoftIdP,
    validateMicrosoftIdPConfig,
    // Custom IdP
    customIdPs,
    customIdPsLimit,
    createCustomIdP,
    listCustomIdPs,
    updateCustomIdP,
    deleteCustomIdP,
    validateCustomIdPConfig,
    discoverOIDCEndpoints,
    // Common
    idPStatus,
    loading,
    fetchIdPStatus,
    // Realm Login Settings
    realmLoginSettings,
    fetchRealmLoginSettings,
    updateRealmLoginSettings,
  } = useApi();

  useEffect(() => {
    // Check if we're in customer context
    const customerContext = isCustomerSubdomain();
    const customerIdFromSubdomain = getCustomerIdFromSubdomain();

    setIsCustomerContext(customerContext);
    setCustomerId(customerIdFromSubdomain);

    // Fetch initial data
    const fetchInitialData = async () => {
      setIsInitialLoading(true);
      try {
        const fetchPromises = [
          listGoogleIdPs(),
          listMicrosoftIdPs(),
          listCustomIdPs(),
          fetchIdPStatus(),
        ];

        // Fetch login settings only in customer context
        if (customerContext) {
          fetchPromises.push(fetchRealmLoginSettings());
        }

        await Promise.all(fetchPromises);
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const refreshPromises = [
        listGoogleIdPs(),
        listMicrosoftIdPs(),
        listCustomIdPs(),
        fetchIdPStatus(),
      ];

      // Include login settings refresh in customer context
      if (isCustomerContext) {
        refreshPromises.push(fetchRealmLoginSettings());
      }

      await Promise.all(refreshPromises);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLoginSettingsToggle = async (event) => {
    const newValue = event.target.checked;

    setIsUpdatingLoginSettings(true);
    try {
      await updateRealmLoginSettings(newValue);
    } finally {
      setIsUpdatingLoginSettings(false);
    }
  };

  // Count configured providers (all are now arrays)
  const configuredCount = googleIdPs.length + microsoftIdPs.length + customIdPs.length;

  // Determine if IdP-only toggle should be disabled (can't enable without IdPs, but can always disable)
  const isIdpOnlyToggleDisabled = isUpdatingLoginSettings || (configuredCount === 0 && !realmLoginSettings.idpOnlyLogin);

  // Tab styling
  const tabStyle = {
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.9rem',
    minHeight: 56,
    color: '#666666',
    '&.Mui-selected': {
      color: accentColor,
    },
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: accentColor,
                width: 48,
                height: 48,
              }}
            >
              <SecurityIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                Identity Providers
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                  fontWeight: 500,
                }}
              >
                Configure external authentication providers for your organization
              </Typography>
            </Box>
          </Box>

          <Tooltip title="Refresh All">
            <IconButton
              onClick={handleRefresh}
              disabled={isRefreshing}
              sx={{
                color: '#666666',
                backgroundColor: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#F0F4F8',
                  borderColor: accentColor,
                  color: accentColor,
                },
                '&:disabled': {
                  color: '#BDBDBD',
                  backgroundColor: '#F9FBFD',
                },
              }}
            >
              {isRefreshing ? (
                <CircularProgress size={20} sx={{ color: accentColor }} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
      </motion.div>

      {/* Status Overview */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <SecurityIcon sx={{ fontSize: 24, color: accentColor }} />
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#253E66',
            }}
          >
            Identity Providers Status
          </Typography>
        </Box>

        <Divider sx={{ mb: 2, borderColor: '#E5E5E5' }} />

        {isInitialLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: accentColor, mr: 2 }} />
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                color: '#666666',
                fontWeight: 500,
              }}
            >
              Loading status...
            </Typography>
          </Box>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#F9FBFD',
                  borderRadius: '6px',
                  border: '1px solid #E5E5E5',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Total Configured
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: accentColor,
                    fontWeight: 700,
                    fontFamily: 'Metropolis, sans-serif',
                  }}
                >
                  {configuredCount}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#F9FBFD',
                  borderRadius: '6px',
                  border: '1px solid #E5E5E5',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Google IdP
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={googleIdPs.length > 0 ? `${googleIdPs.length} Configured` : 'Not Configured'}
                    size="small"
                    icon={googleIdPs.length > 0 ? <CheckCircleIcon /> : undefined}
                    sx={{
                      backgroundColor: googleIdPs.length > 0 ? '#E8F8F0' : '#F9FBFD',
                      color: googleIdPs.length > 0 ? '#27AE60' : '#999999',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                      border: `1px solid ${googleIdPs.length > 0 ? '#27AE60' : '#E5E5E5'}20`,
                      '& .MuiChip-icon': {
                        color: '#27AE60',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#F9FBFD',
                  borderRadius: '6px',
                  border: '1px solid #E5E5E5',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Microsoft IdP
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={microsoftIdPs.length > 0 ? `${microsoftIdPs.length} Configured` : 'Not Configured'}
                    size="small"
                    icon={microsoftIdPs.length > 0 ? <CheckCircleIcon /> : undefined}
                    sx={{
                      backgroundColor: microsoftIdPs.length > 0 ? '#E8F8F0' : '#F9FBFD',
                      color: microsoftIdPs.length > 0 ? '#27AE60' : '#999999',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                      border: `1px solid ${microsoftIdPs.length > 0 ? '#27AE60' : '#E5E5E5'}20`,
                      '& .MuiChip-icon': {
                        color: '#27AE60',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#F9FBFD',
                  borderRadius: '6px',
                  border: '1px solid #E5E5E5',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Custom IdP
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={customIdPs.length > 0 ? `${customIdPs.length} Configured` : 'Not Configured'}
                    size="small"
                    icon={customIdPs.length > 0 ? <CheckCircleIcon /> : undefined}
                    sx={{
                      backgroundColor: customIdPs.length > 0 ? '#E8F8F0' : '#F9FBFD',
                      color: customIdPs.length > 0 ? '#27AE60' : '#999999',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                      border: `1px solid ${customIdPs.length > 0 ? '#27AE60' : '#E5E5E5'}20`,
                      '& .MuiChip-icon': {
                        color: '#27AE60',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
          </motion.div>
        )}
      </Paper>
      </motion.div>

      {/* Login Settings Card - Only shown in customer context */}
      {isCustomerContext && (
        <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <SettingsIcon sx={{ fontSize: 24, color: accentColor }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                fontSize: '1rem',
                color: '#253E66',
              }}
            >
              Login Settings
            </Typography>
          </Box>

          <Divider sx={{ mb: 2, borderColor: '#E5E5E5' }} />

          {isInitialLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} sx={{ color: accentColor, mr: 2 }} />
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  color: '#666666',
                  fontWeight: 500,
                }}
              >
                Loading login settings...
              </Typography>
            </Box>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
            <Box
              sx={{
                p: 2,
                backgroundColor: '#F9FBFD',
                borderRadius: '6px',
                border: '1px solid #E5E5E5',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      color: '#253E66',
                      mb: 0.5,
                    }}
                  >
                    IdP-Only Login
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      color: '#666666',
                    }}
                  >
                    When enabled, users can only sign in using configured Identity Providers.
                    Direct username/password login will be disabled.
                  </Typography>
                  {configuredCount === 0 && !realmLoginSettings.idpOnlyLogin && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        color: '#F39C12',
                        fontStyle: 'italic',
                        display: 'block',
                        mt: 0.5,
                      }}
                    >
                      Configure at least one Identity Provider to enable this option.
                    </Typography>
                  )}
                </Box>
                <Box sx={{ ml: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={realmLoginSettings.idpOnlyLogin}
                        onChange={handleLoginSettingsToggle}
                        disabled={isIdpOnlyToggleDisabled}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: accentColor,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: accentColor,
                          },
                        }}
                      />
                    }
                    label={
                      isUpdatingLoginSettings ? (
                        <CircularProgress size={16} sx={{ color: accentColor }} />
                      ) : (
                        <Chip
                          label={realmLoginSettings.idpOnlyLogin ? 'Enabled' : 'Disabled'}
                          size="small"
                          sx={{
                            backgroundColor: realmLoginSettings.idpOnlyLogin ? '#E8F8F0' : '#F9FBFD',
                            color: realmLoginSettings.idpOnlyLogin ? '#27AE60' : '#999999',
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 500,
                            border: `1px solid ${realmLoginSettings.idpOnlyLogin ? '#27AE60' : '#E5E5E5'}20`,
                          }}
                        />
                      )
                    }
                    labelPlacement="start"
                  />
                </Box>
              </Box>
            </Box>
            </motion.div>
          )}
        </Paper>
        </motion.div>
      )}

      {/* Info Alert */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Alert
        severity="info"
        icon={<InfoIcon />}
        sx={{
          backgroundColor: accentColorLight,
          border: `1px solid ${accentColor}20`,
          borderRadius: '8px',
          '& .MuiAlert-icon': { color: accentColor },
          '& .MuiAlert-message': {
            fontFamily: 'Metropolis, sans-serif',
            color: '#253E66',
          },
        }}
      >
        <Typography variant="body2" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
          Identity Providers allow your users to authenticate using external services like Google, Microsoft, or custom OIDC providers.
          You can configure up to 10 Identity Providers of each type.
          {isCustomerContext
            ? ' Configure providers specific to your customer realm.'
            : ' These configurations are global for your organization.'}
        </Typography>
      </Alert>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: accentColor,
              height: 3,
            },
            px: 1,
          }}
        >
          <Tab
            label="Google"
            icon={<GoogleIcon />}
            iconPosition="start"
            sx={tabStyle}
          />
          <Tab
            label="Microsoft"
            icon={<MicrosoftIcon />}
            iconPosition="start"
            sx={tabStyle}
          />
          <Tab
            label="Custom OIDC"
            icon={<SecurityIcon />}
            iconPosition="start"
            sx={tabStyle}
          />
        </Tabs>
      </Paper>
      </motion.div>

      {/* Tab Content */}
      <motion.div variants={sectionVariants}>
      <Box>
        {tabValue === 0 && (
          <GoogleIdPList
            googleIdPs={googleIdPs}
            googleIdPsLimit={googleIdPsLimit}
            createGoogleIdP={createGoogleIdP}
            updateGoogleIdP={updateGoogleIdP}
            deleteGoogleIdP={deleteGoogleIdP}
            listGoogleIdPs={listGoogleIdPs}
            validateGoogleIdPConfig={validateGoogleIdPConfig}
            loading={loading}
            isInitialLoading={isInitialLoading}
          />
        )}
        {tabValue === 1 && (
          <MicrosoftIdPList
            microsoftIdPs={microsoftIdPs}
            microsoftIdPsLimit={microsoftIdPsLimit}
            createMicrosoftIdP={createMicrosoftIdP}
            updateMicrosoftIdP={updateMicrosoftIdP}
            deleteMicrosoftIdP={deleteMicrosoftIdP}
            listMicrosoftIdPs={listMicrosoftIdPs}
            validateMicrosoftIdPConfig={validateMicrosoftIdPConfig}
            loading={loading}
            isInitialLoading={isInitialLoading}
          />
        )}
        {tabValue === 2 && (
          <CustomIdPList
            customIdPs={customIdPs}
            customIdPsLimit={customIdPsLimit}
            createCustomIdP={createCustomIdP}
            updateCustomIdP={updateCustomIdP}
            deleteCustomIdP={deleteCustomIdP}
            listCustomIdPs={listCustomIdPs}
            validateCustomIdPConfig={validateCustomIdPConfig}
            discoverOIDCEndpoints={discoverOIDCEndpoints}
            loading={loading}
            isInitialLoading={isInitialLoading}
          />
        )}
      </Box>
      </motion.div>
    </motion.div>
  );
};

export default IdentityProviders;
